import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, ArrowLeft, BookOpen, Eye, EyeOff, CalendarClock } from "lucide-react";
import { Field, inputCls, Toggle } from "../forms";
import { adminCreateArticle, adminUpdateArticle, adminDeleteArticle } from "../../lib/api";

const SERIES = [
  "On peut en parler ?",
  "En termes simples",
  "C'est plus nuancé que ça",
  "Entre nous",
  "Et si on regardait ça autrement ?",
];

const CATS = ["Sexualité", "Relations", "Intimité", "Bien-être", "Société & innovation"];

const slugify = (s) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const blocksToText = (blocks = []) =>
  blocks
    .map((b) => (b.type === "h2" ? `## ${b.text}` : b.type === "quote" ? `> ${b.text}` : b.text))
    .join("\n\n");

const textToBlocks = (text) =>
  text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      if (p.startsWith("## ")) return { type: "h2", text: p.slice(3).trim() };
      if (p.startsWith("> ")) return { type: "quote", text: p.slice(2).trim() };
      return { type: "p", text: p.replace(/\s*\n\s*/g, " ") };
    });

const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const localInputToIso = (v) => (v ? new Date(v).toISOString() : null);

const formatPublishAt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-CA", {
      year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const isScheduled = (a) => a.published && a.publish_at && new Date(a.publish_at) > new Date();

const EMPTY = {
  slug: "", title: "", serie: SERIES[0], cat: CATS[0],
  time: "5 min", date: "", img: "", dek: "", contentText: "",
  published: true, publish_at: "",
};

const selectCls = `${inputCls} cursor-pointer`;

export function ArticlesPanel({ token, articles, onChanged }) {
  const [draft, setDraft] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");

  const startNew = () => {
    setSlugTouched(false);
    setDraft({ ...EMPTY });
  };

  const startEdit = (a) => {
    setSlugTouched(true);
    setDraft({
      id: a.id, slug: a.slug, title: a.title, serie: a.serie || SERIES[0],
      cat: a.cat || CATS[0], time: a.time, date: a.date, img: a.img,
      dek: a.dek, contentText: blocksToText(a.content), published: a.published,
      publish_at: isoToLocalInput(a.publish_at),
    });
  };

  const setField = (key) => (e) => {
    const v = e.target.value;
    setDraft((d) => {
      const next = { ...d, [key]: v };
      if (key === "title" && !d.id && !slugTouched) next.slug = slugify(v);
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const content = textToBlocks(draft.contentText);
    if (content.length === 0) {
      toast.error("L'article doit contenir au moins un paragraphe.");
      return;
    }
    setSaving(true);
    const payload = {
      slug: draft.slug, title: draft.title, serie: draft.serie, cat: draft.cat,
      time: draft.time, date: draft.date, img: draft.img, dek: draft.dek,
      content, published: draft.published, publish_at: localInputToIso(draft.publish_at),
    };
    try {
      if (draft.id) {
        await adminUpdateArticle(token, draft.id, payload);
        toast.success("Article mis à jour, les changements sont en ligne.");
      } else {
        await adminCreateArticle(token, payload);
        toast.success(payload.publish_at ? "Article programmé : il paraîtra à la date choisie."
          : payload.published ? "Article publié : il est en ligne." : "Brouillon enregistré (non publié).");
      }
      setDraft(null);
      onChanged();
    } catch (err) {
      toast.error(err.message === "request_failed" ? "Impossible d'enregistrer l'article." : err.message);
    } finally {
      setSaving(false);
    }
  };

  const quickToggle = async (a) => {
    setBusyId(a.id);
    try {
      await adminUpdateArticle(token, a.id, {
        slug: a.slug, title: a.title, serie: a.serie, cat: a.cat, time: a.time,
        date: a.date, img: a.img, dek: a.dek, content: a.content,
        published: !a.published, publish_at: a.publish_at || null,
      });
      toast.success(a.published ? "Article retiré du site (brouillon)." : "Article publié : il est en ligne.");
      onChanged();
    } catch {
      toast.error("Impossible de changer la publication.");
    } finally {
      setBusyId("");
    }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Supprimer définitivement « ${a.title} » ? Cette action est irréversible.`)) return;
    setBusyId(a.id);
    try {
      await adminDeleteArticle(token, a.id);
      toast.success("Article supprimé.");
      onChanged();
    } catch {
      toast.error("Suppression impossible.");
    } finally {
      setBusyId("");
    }
  };

  if (draft) {
    return (
      <form data-testid="article-editor-form" onSubmit={onSubmit}
        className="max-w-3xl rounded-[1.75rem] border border-linen bg-shell p-8 md:p-10">
        <button type="button" onClick={() => setDraft(null)} data-testid="article-editor-back"
          className="group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:text-clay">
          <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
          Retour à la liste
        </button>
        <h2 className="mt-6 font-serif text-2xl font-light tracking-tight md:text-3xl">
          {draft.id ? "Modifier" : "Écrire"} <em className="text-clay">une conversation.</em>
        </h2>

        <div className="mt-8 space-y-7">
          <Field label="Titre *" htmlFor="art-title">
            <input id="art-title" data-testid="article-title-input" required minLength={3} maxLength={200}
              value={draft.title} onChange={setField("title")}
              placeholder="Ex. : Parler de désir sans gêne" className={inputCls} />
          </Field>
          <div className="grid gap-7 md:grid-cols-2">
            <Field label="Adresse (slug) *" htmlFor="art-slug">
              <input id="art-slug" data-testid="article-slug-input" required minLength={2} maxLength={200}
                pattern="[a-z0-9]+(-[a-z0-9]+)*" value={draft.slug}
                onChange={(e) => { setSlugTouched(true); setField("slug")(e); }}
                placeholder="parler-de-desir-sans-gene" className={inputCls} />
            </Field>
            <Field label="Date affichée" htmlFor="art-date">
              <input id="art-date" data-testid="article-date-input" maxLength={40}
                value={draft.date} onChange={setField("date")}
                placeholder="Ex. : Juillet 2026" className={inputCls} />
            </Field>
          </div>
          <div className="grid gap-7 md:grid-cols-3">
            <Field label="Série" htmlFor="art-serie">
              <select id="art-serie" data-testid="article-serie-select" value={draft.serie}
                onChange={setField("serie")} className={selectCls}>
                {SERIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Catégorie" htmlFor="art-cat">
              <select id="art-cat" data-testid="article-cat-select" value={draft.cat}
                onChange={setField("cat")} className={selectCls}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Temps de lecture" htmlFor="art-time">
              <input id="art-time" data-testid="article-time-input" maxLength={20}
                value={draft.time} onChange={setField("time")}
                placeholder="5 min" className={inputCls} />
            </Field>
          </div>
          <Field label="Image (URL)" htmlFor="art-img">
            <input id="art-img" data-testid="article-img-input" maxLength={500}
              value={draft.img} onChange={setField("img")}
              placeholder="https://…" className={inputCls} />
          </Field>
          <Field label="Chapeau (accroche d'ouverture)" htmlFor="art-dek">
            <textarea id="art-dek" data-testid="article-dek-input" rows={2} maxLength={500}
              value={draft.dek} onChange={setField("dek")}
              placeholder="Une phrase ou deux qui donnent envie de lire…" className={`${inputCls} resize-none`} />
          </Field>
          <Field label="Contenu *" htmlFor="art-content">
            <textarea id="art-content" data-testid="article-content-input" required rows={14}
              value={draft.contentText} onChange={setField("contentText")}
              placeholder={"Séparez les paragraphes par une ligne vide.\n\n## Pour un sous-titre\n\n> Pour une citation"}
              className={`${inputCls} resize-y font-mono text-sm leading-relaxed`} />
          </Field>
          <div className="flex items-center justify-between gap-6 rounded-[1.75rem] border border-linen bg-cream/60 p-6">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Publication</p>
              <p className="mt-1 text-sm text-smoke" data-testid="article-publish-state">
                {draft.published ? "Visible sur le site dès l'enregistrement" : "Brouillon : invisible sur le site"}
              </p>
            </div>
            <Toggle checked={draft.published} onChange={(v) => setDraft((d) => ({ ...d, published: v }))} testId="article-publish-toggle" />
          </div>
          <Field label="Publication programmée (optionnel)" htmlFor="art-publish-at">
            <input id="art-publish-at" data-testid="article-publish-at-input" type="datetime-local"
              value={draft.publish_at} onChange={setField("publish_at")} className={inputCls} />
          </Field>
          <p className="-mt-4 text-xs italic text-ash">
            Choisissez une date et une heure : l'article restera invisible jusqu'à ce moment, puis paraîtra tout seul.
            Laissez vide pour une publication immédiate.
          </p>
          <button type="submit" data-testid="article-save-button" disabled={saving}
            className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
            {saving ? "Enregistrement" : draft.id ? "Enregistrer les changements" : "Enregistrer l'article"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-smoke">
          <strong>{articles.length}</strong> article(s) · {articles.filter((a) => a.published && !isScheduled(a)).length} en ligne
          {articles.some(isScheduled) && ` · ${articles.filter(isScheduled).length} programmé(s)`}
        </p>
        <button onClick={startNew} data-testid="article-new-button"
          className="group inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
          <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
          Nouvel article
        </button>
      </div>

      <div className="mt-6 space-y-4">
        {articles.length === 0 ? (
          <p className="rounded-[1.75rem] border border-dashed border-linen py-14 text-center font-serif text-xl font-light italic text-smoke">
            Aucun article pour l'instant. La première conversation commence ici.
          </p>
        ) : articles.map((a, i) => (
          <div key={a.id} data-testid={`admin-article-${i + 1}`}
            className="flex flex-wrap items-center justify-between gap-4 rounded-[1.75rem] border border-linen bg-shell p-6">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-serif text-lg font-light leading-snug tracking-tight">{a.title}</p>
                <span data-testid={`admin-article-${i + 1}-status`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] ${
                  isScheduled(a) ? "bg-sand/70 text-clay" : a.published ? "bg-sage/60 text-smoke" : "bg-clay/10 text-clay"
                }`}>
                  {isScheduled(a) ? <CalendarClock className="h-3 w-3" /> : a.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  {isScheduled(a) ? "Programmé" : a.published ? "En ligne" : "Brouillon"}
                </span>
              </div>
              <p className="mt-1.5 text-xs text-ash">
                {a.serie} · {a.cat}{a.date ? ` · ${a.date}` : ""} · /conversations/{a.slug}
                {isScheduled(a) && (
                  <span className="text-clay"> · paraît le {formatPublishAt(a.publish_at)}</span>
                )}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button onClick={() => quickToggle(a)} disabled={busyId === a.id} data-testid={`admin-article-${i + 1}-toggle`}
                className="inline-flex items-center gap-2 rounded-full border border-linen px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-smoke transition-colors duration-300 hover:border-clay hover:text-clay disabled:opacity-60">
                {busyId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : a.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {a.published ? "Dépublier" : "Publier"}
              </button>
              <button onClick={() => startEdit(a)} data-testid={`admin-article-${i + 1}-edit`}
                className="inline-flex items-center gap-2 rounded-full border border-linen px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-smoke transition-colors duration-300 hover:border-clay hover:text-clay">
                <Pencil className="h-3.5 w-3.5" />
                Modifier
              </button>
              <button onClick={() => onDelete(a)} disabled={busyId === a.id} data-testid={`admin-article-${i + 1}-delete`}
                className="inline-flex items-center gap-2 rounded-full border border-linen px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-smoke transition-colors duration-300 hover:border-clay hover:text-clay disabled:opacity-60">
                <Trash2 className="h-3.5 w-3.5" />
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
